/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useContext, useEffect, useState } from "react";
import { Column, GenericTable } from "../../components/GenericTable";
import { useMatchmakingService } from "../../services/backend/MatchmakingService";
import { Alert, AlertColor, Snackbar } from "@mui/material";
import { AppContext } from "../../utils/AppContext";
import { useSearchParams } from "react-router-dom";

interface RulesetType {
  id: string;
  name: string;
  envName: string;
  description: string;
  ruleSet: string;
}

const ruleSetExample = {
  rules: [],
  teams: [
    {
      name: "neighborhood",
      minPlayers: 2,
      maxPlayers: 6,
    },
  ],
  expansions: [
    {
      targetType: "teams",
      targetName: "neighborhood",
      propertyName: "minPlayers",
      steps: [
        {
          value: 1,
          waitTimeSeconds: 15,
        },
      ],
    },
  ],
  playerAttributes: [],
};

const columns: Column<RulesetType>[] = [
  { key: "name", label: "Name", type: "string" },
  { key: "envName", label: "Environment", type: "string" },
  { key: "description", label: "Description", type: "string" },
  { key: "ruleSet", label: "Ruleset Object", type: "object", default: JSON.stringify(ruleSetExample) },
];

const RulesetViewer = () => {
  const context = useContext(AppContext);
  const [searchParams] = useSearchParams();
  const rulesetNameQuery = searchParams.get("rulesetName")?.toLowerCase();
  const matchmakingService = useMatchmakingService();

  const [rulesetList, setRulesetList] = useState<RulesetType[]>([]);

  // Alerts
  const [alertMessage, setAlertMessage] = useState<string | undefined>(undefined);
  const [alertSeverity, setAlertSeverity] = useState<AlertColor>("info");

  const fetchRulesetList = async () => {
    try {
      const { ruleSets } = await matchmakingService.listRulesets();
      const filteredRulesets = ruleSets.filter((ruleset: RulesetType) =>
        rulesetNameQuery ? ruleset.name.toLowerCase().includes(rulesetNameQuery) : true,
      );
      setRulesetList(
        filteredRulesets.map((ruleset: RulesetType, index: string) => ({
          ...ruleset,
          id: ruleset.id || String(index),
        })),
      );
    } catch (e: any) {
      setAlertMessage(e.debugMessage || "Could not fetch the list of log files.");
      setAlertSeverity("error");
    }
  };

  const alertCloseHandler = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === "clickaway") {
      return;
    }
    setAlertMessage(undefined);
  };

  const handleAddRow = async (newRow: RulesetType) => {
    try {
      await matchmakingService.createRuleset({ ...newRow, ruleSet: JSON.parse(newRow.ruleSet) });
      setRulesetList((prevList) => [...prevList, newRow]);
    } catch (e) {
      console.error(e);
    }
  };

  const handleEditRow = async (updatedRow: RulesetType) => {
    const row = rulesetList.find((r) => r.id === updatedRow.id);

    if (!row) {
      setAlertMessage("Row not found");
      setAlertSeverity("error");
      return;
    }
    await matchmakingService.updateRuleset(row.name, { ...updatedRow, ruleSet: updatedRow.ruleSet });
    setRulesetList((prevList) => prevList.map((row) => (row.id === updatedRow.id ? { ...row, ...updatedRow } : row)));
  };

  const handleDeleteRow = async (rowId: string) => {
    const row = rulesetList.find((r) => r.id === rowId);

    if (!row) {
      setAlertMessage("Row not found");
      setAlertSeverity("error");
      return;
    }

    await matchmakingService.deleteRuleset(row.name);
    setRulesetList((prevList) => prevList.filter((r) => r.id !== rowId)); // Update state
  };
  useEffect(() => {
    context?.setTitle("Matchmaking RuleSet");
    context?.setTargetAPI("environment");
    if (context?.selectedEnvironment) {
      (async () => await fetchRulesetList())();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context?.selectedEnvironment]);

  useEffect(() => {
    if (rulesetNameQuery) {
      (async () => {
        await fetchRulesetList();
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rulesetNameQuery]);

  return (
    <div>
      <GenericTable
        rows={rulesetList}
        columns={columns}
        enablePagination={true}
        enableSorting={true}
        onAddRow={handleAddRow}
        onEditRow={handleEditRow}
        onDeleteRow={handleDeleteRow}
      />
      <Snackbar open={alertMessage !== undefined} autoHideDuration={5000} onClose={alertCloseHandler}>
        <Alert onClose={alertCloseHandler} severity={alertSeverity} sx={{ width: "100%" }}>
          {alertMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default RulesetViewer;
